import type { IconName } from '../components/Icon';

/**
 * Die Ziele der Anwendung -- an einer Stelle.
 *
 * Sie standen bisher nur im JSX der Navigation. Seit es die Befehlspalette
 * gibt, braucht sie dieselbe Liste: dieselben Ziele, dieselben Namen,
 * dieselbe Rechtepruefung. Zwei Listen waeren nach dem naechsten neuen
 * Bereich verschieden -- und die Palette fuehrte an eine Stelle, die es in
 * der Navigation nicht mehr gibt.
 *
 * **Das Recht blendet aus, es sichert nicht ab.** Wer den Eintrag nicht
 * sieht, kann die Adresse trotzdem eintippen -- der Server prueft jeden
 * Aufruf (docs/04, Abschnitt 5). Was hier steht, erspart den Weg in eine
 * Ansicht, die sich nur mit Fehlermeldungen fuellt.
 *
 * **Die Gliederung folgt dem Gegenstand, nicht dem Haeufigkeitsgefuehl.**
 * Tickets, Kunden, Auswertung -- und darunter die Einstellungen. Ein Block
 * mit einem einzigen Eintrag ist keiner: Er traegt eine Ueberschrift, die nur
 * sich selbst ankuendigt.
 *
 * **Warum die Einstellungen Gruppen haben.** Zwanzig Eintraege, die man
 * einmal einrichtet und danach im Quartal anfasst, truegen sonst dasselbe
 * Gewicht wie "Tickets". Sie sind deshalb in Gruppen geteilt, die zugeklappt
 * beginnen; die Gruppe, in der die aufgerufene Seite liegt, oeffnet sich von
 * selbst -- niemand soll suchen muessen, wo er gerade steht.
 */
export type NavigationGroup =
  // Die Seitenleiste, flach
  | 'home'
  | 'tickets'
  | 'customers'
  | 'reporting'
  | 'help'
  // Die Statusseite -- nicht als Zeile, sondern als Build-Nummer am Fuss
  // der Leiste (``features/system/VersionLink.tsx``). Sie steht trotzdem in
  // dieser Liste, damit die Befehlspalette sie findet.
  | 'status'
  // Die Einstellungen, aufklappbar
  | 'ticketSettings'
  | 'templates'
  | 'channels'
  | 'access'
  | 'integrations'
  | 'privacy';

/** Eine Zahl neben dem Eintrag. Ein Name und keine Abfrage: Diese Liste
 *  laedt nichts, sie sagt nur, welche Zahl die Leiste holen soll. */
export type NavigationBadge = 'inbound';

export interface NavigationEntry {
  /** Das Ziel. Als Zeichenkette und nicht als Routenobjekt: Diese Liste
   *  darf den Router nicht laden, sonst haengt die Navigation an ihm. */
  to: string;
  /** Schluessel in den Sprachdateien. Kein deutscher Text im Code. */
  labelKey: string;
  icon: IconName;
  /** Ohne dieses Recht erscheint der Eintrag nicht. */
  permission?: string;
  /** Zu welcher Gruppe der Navigation er gehoert. */
  group: NavigationGroup;
  /** Welche Zahl daneben steht, wenn sie groesser als null ist. */
  badge?: NavigationBadge;
}

/** Eine Gruppe innerhalb eines Blocks. Nur aufklappbare tragen eine
 *  Beschriftung -- eine Ueberschrift ueber einer flachen Gruppe waere
 *  dieselbe Zeile zweimal. */
export interface NavigationGroupRef {
  id: NavigationGroup;
  labelKey?: string;
}

/** Ein Block der Seitenleiste. */
export interface NavigationSection {
  id: string;
  /** Die Ueberschrift. Der erste Block hat keine: Die Startseite braucht
   *  kein Schild darueber. */
  labelKey?: string;
  /** Ob die Gruppen dieses Blocks auf- und zuklappen. Nur die Einstellungen
   *  tun das -- sie sind der einzige Block, der laenger ist als der Blick. */
  collapsible?: boolean;
  /** Abgesetzt am Fuss der Leiste. */
  footer?: boolean;
  groups: readonly NavigationGroupRef[];
}

export const NAVIGATION: readonly NavigationEntry[] = [
  { to: '/app/dashboard', labelKey: 'nav.dashboard', icon: 'dashboard', group: 'home' },

  // ---- Tickets -----------------------------------------------------------
  { to: '/app/tickets', labelKey: 'nav.tickets', icon: 'tickets', group: 'tickets' },
  { to: '/app/board', labelKey: 'nav.board', icon: 'board', group: 'tickets' },
  // Die Anfragen -- F5-03. Was aus einem Postfach kam und nicht von selbst
  // zum Vorgang wurde. Sie standen nur als Knopf neben der Ticketliste und
  // als Reiter in der E-Mail-Einrichtung: an der einen Stelle zu versteckt,
  // an der anderen falsch -- eine Einstellung sind sie nicht. Die Zahl
  // daneben ist der Grund fuer den Eintrag: Eine Warteschlange, die man erst
  // oeffnen muss, um zu sehen, dass sie leer ist, wird nicht geoeffnet.
  {
    to: '/app/anfragen',
    labelKey: 'nav.inbound',
    icon: 'inbox',
    permission: 'mail.triage',
    group: 'tickets',
    badge: 'inbound',
  },
  // Die eigenen Termine -- F1-54.
  {
    to: '/app/calendar',
    labelKey: 'nav.calendar',
    icon: 'calendar',
    permission: 'ticket.read',
    group: 'tickets',
  },
  // Bei den Tickets und nicht bei den Kunden: Gebraucht wird sie, waehrend
  // jemand ein Ticket loest. Die Artikel zu einem Kunden stehen ohnehin als
  // Reiter in seiner Akte.
  { to: '/app/knowledge', labelKey: 'nav.knowledge', icon: 'knowledge', group: 'tickets' },

  // ---- Kunden ------------------------------------------------------------
  { to: '/app/crm', labelKey: 'nav.crm', icon: 'crm', group: 'customers' },
  // Die Sicherungsuebersicht -- F5-01. Sie zeigt die Sicherungen der
  // Kunden und wird gelesen wie eine Liste, nicht eingerichtet wie eine
  // Stammdatenliste. Ein eigener Block "Betrieb" fuer einen einzigen
  // Eintrag war eine Ueberschrift, die nur sich selbst ankuendigte.
  {
    to: '/app/backup',
    labelKey: 'nav.backup',
    icon: 'backup',
    permission: 'backup.read',
    group: 'customers',
  },

  // ---- Auswertung --------------------------------------------------------
  { to: '/app/time-report', labelKey: 'nav.timeReport', icon: 'time', group: 'reporting' },
  { to: '/app/reports', labelKey: 'nav.reports', icon: 'reports', group: 'reporting' },

  // ---- Einstellungen: Tickets --------------------------------------------
  // Wie ein Vorgang eingeordnet wird und was mit ihm von selbst geschieht:
  // Auswahllisten, Kategorien, Matrix, eigene Felder, Fristen und Regeln.
  {
    to: '/app/administration/master-data',
    labelKey: 'nav.masterData',
    icon: 'masterData',
    permission: 'masterdata.write',
    group: 'ticketSettings',
  },
  {
    to: '/app/administration/categories',
    labelKey: 'nav.categories',
    icon: 'categories',
    permission: 'masterdata.write',
    group: 'ticketSettings',
  },
  {
    to: '/app/administration/priority-matrix',
    labelKey: 'nav.priorityMatrix',
    icon: 'matrix',
    permission: 'masterdata.write',
    group: 'ticketSettings',
  },
  {
    to: '/app/administration/custom-fields',
    labelKey: 'nav.customFields',
    icon: 'customFields',
    permission: 'masterdata.write',
    group: 'ticketSettings',
  },
  {
    to: '/app/administration/sla',
    labelKey: 'nav.sla',
    icon: 'sla',
    permission: 'masterdata.write',
    group: 'ticketSettings',
  },
  {
    to: '/app/administration/automation',
    labelKey: 'nav.automation',
    icon: 'automation',
    permission: 'masterdata.write',
    group: 'ticketSettings',
  },

  // ---- Einstellungen: Vorlagen -------------------------------------------
  // Alles, was Tipparbeit spart. Sie unterscheiden sich im Umfang -- ein
  // Baustein ist ein Absatz, eine Vorlage ein halbes Ticket --, aber wer
  // eines sucht, sucht sie alle an derselben Stelle.
  {
    to: '/app/administration/text-snippets',
    labelKey: 'nav.textSnippets',
    icon: 'snippets',
    permission: 'masterdata.write',
    group: 'templates',
  },
  {
    to: '/app/administration/ticket-templates',
    labelKey: 'nav.ticketTemplates',
    icon: 'templates',
    permission: 'masterdata.write',
    group: 'templates',
  },
  {
    to: '/app/administration/macros',
    labelKey: 'nav.macros',
    icon: 'macros',
    permission: 'masterdata.write',
    group: 'templates',
  },
  {
    to: '/app/administration/checklist-templates',
    labelKey: 'nav.checklistTemplates',
    icon: 'checklists',
    permission: 'masterdata.write',
    group: 'templates',
  },

  // ---- Einstellungen: Kanaele --------------------------------------------
  // Alles, was nach aussen wirkt: woher Vorgaenge kommen und was Kunden
  // dabei sehen.
  {
    to: '/app/administration/mail',
    labelKey: 'nav.mail',
    icon: 'mail',
    permission: 'masterdata.write',
    group: 'channels',
  },
  {
    to: '/app/administration/web-forms',
    labelKey: 'nav.webForms',
    icon: 'webForms',
    permission: 'masterdata.write',
    group: 'channels',
  },
  {
    to: '/app/administration/portal',
    labelKey: 'nav.portalBranding',
    icon: 'branding',
    permission: 'masterdata.write',
    group: 'channels',
  },
  // Ankuendigungen -- F3-10. Sie standen als Reiter in der E-Mail-
  // Einrichtung, gehen aber nicht per Mail hinaus: Sie erscheinen als Banner
  // in der Anwendung **und** im Kundenportal. Mit ``announcement.write`` und
  // nicht mit ``masterdata.write`` -- dieses Recht hat auch der Bearbeiter
  // am Telefon, der die Stoerung bemerkt (Migration 0015), und hinter dem
  // Verwaltungsrecht fand er den Reiter nicht.
  {
    to: '/app/administration/announcements',
    labelKey: 'nav.announcements',
    icon: 'announcement',
    permission: 'announcement.write',
    group: 'channels',
  },

  // ---- Einstellungen: Benutzer & Rechte ----------------------------------
  {
    to: '/app/administration/users',
    labelKey: 'nav.users',
    icon: 'users',
    permission: 'masterdata.write',
    group: 'access',
  },
  // Rollen -- F5-19. Direkt unter den Benutzern: Dort wird eine Rolle
  // vergeben, hier steht, was sie gewaehrt. Im Menue mit ``roles.manage``;
  // die Seite selbst liest mit ``users.read`` und ist von den Benutzern aus
  // verlinkt -- wer eine Rolle vergibt, soll nachsehen koennen, was sie darf.
  {
    to: '/app/administration/roles',
    labelKey: 'nav.roles',
    icon: 'roles',
    permission: 'roles.manage',
    group: 'access',
  },

  // ---- Einstellungen: Schnittstellen -------------------------------------
  // Drei Richtungen, drei Eintraege: Mit einem API-Token meldet sich eine
  // Maschine **hier** an; ein Webhook meldet **von hier** an einen fremden
  // Dienst; unter "Fremddienste" liegen die Zugangsdaten, mit denen sich
  // dieses System **dort** anmeldet. In einer Liste widerriefe irgendwann
  // jemand das Falsche. Ein Token ist weiterhin kein Mitarbeiter -- darum
  // steht es nicht bei den Benutzern.
  {
    to: '/app/administration/api-tokens',
    labelKey: 'nav.apiTokens',
    icon: 'apiTokens',
    permission: 'masterdata.write',
    group: 'integrations',
  },
  {
    to: '/app/administration/webhooks',
    labelKey: 'nav.webhooks',
    icon: 'webhook',
    permission: 'masterdata.write',
    group: 'integrations',
  },
  {
    to: '/app/administration/tokens',
    labelKey: 'nav.tokens',
    icon: 'tokens',
    permission: 'backup.manage',
    group: 'integrations',
  },

  // ---- Einstellungen: Datenschutz ----------------------------------------
  // Beides traegt ``privacy.manage`` und nicht ``masterdata.write``: Der
  // Auskunftsexport zieht alles zusammen, was ueber eine Person gespeichert
  // ist, und das Protokoll zeigt alte und neue Werte quer durch alle Module.
  {
    to: '/app/administration/privacy',
    labelKey: 'nav.privacy',
    icon: 'privacy',
    permission: 'privacy.manage',
    group: 'privacy',
  },
  {
    to: '/app/administration/audit',
    labelKey: 'nav.audit',
    icon: 'audit',
    permission: 'privacy.manage',
    group: 'privacy',
  },

  // Ganz unten, abgesetzt und ohne Rechtepruefung: Das Handbuch beschreibt
  // auch, was der Lesende nicht darf -- und genau das ist die Frage, mit der
  // er hierherkommt. Es gehoert zu keinem Bereich, weil es von allen handelt.
  { to: '/app/handbuch', labelKey: 'nav.handbook', icon: 'handbook', group: 'help' },

  // Keine eigene Zeile in der Leiste: Die Gruppe ``status`` steht in keinem
  // Block von SIDEBAR. Gezeichnet wird sie als Build-Nummer unter dem
  // Handbuch; hier steht sie fuer die Befehlspalette.
  {
    to: '/app/status',
    labelKey: 'nav.status',
    icon: 'system',
    permission: 'system.read',
    group: 'status',
  },
] as const;

/** Die Bloecke der Seitenleiste, in der Reihenfolge, in der sie stehen. */
export const SIDEBAR: readonly NavigationSection[] = [
  { id: 'home', groups: [{ id: 'home' }] },
  { id: 'tickets', labelKey: 'nav.sectionTickets', groups: [{ id: 'tickets' }] },
  { id: 'customers', labelKey: 'nav.sectionCustomers', groups: [{ id: 'customers' }] },
  { id: 'reporting', labelKey: 'nav.sectionReporting', groups: [{ id: 'reporting' }] },
  {
    id: 'settings',
    labelKey: 'nav.settings',
    collapsible: true,
    groups: [
      { id: 'ticketSettings', labelKey: 'nav.groupTickets' },
      { id: 'templates', labelKey: 'nav.groupTemplates' },
      { id: 'channels', labelKey: 'nav.groupChannels' },
      { id: 'access', labelKey: 'nav.groupAccess' },
      { id: 'integrations', labelKey: 'nav.groupIntegrations' },
      { id: 'privacy', labelKey: 'nav.groupPrivacy' },
    ],
  },
  { id: 'help', footer: true, groups: [{ id: 'help' }] },
] as const;

/** Die Eintraege einer Gruppe, die der Betrachter benutzen darf. */
export function entriesFor(
  group: NavigationGroup,
  permissions: readonly string[],
): NavigationEntry[] {
  return NAVIGATION.filter(
    (entry) =>
      entry.group === group &&
      (entry.permission === undefined || permissions.includes(entry.permission)),
  );
}

/**
 * Die Gruppe, in der die aufgerufene Seite liegt -- oder ``undefined``.
 *
 * Der laengste passende Eintrag gewinnt. Sonst gewaenne ``/app/tickets`` auch
 * auf ``/app/administration/ticket-templates`` nicht, wohl aber gewaenne bei
 * gleicher Behandlung ein kuerzerer Praefix ueber den genaueren Treffer --
 * und die Verwaltung bliebe zu, waehrend man in ihr steht.
 *
 * Verglichen wird auf ganze Abschnitte (``/app/tickets/`` und nicht
 * ``/app/tickets``): Ohne den Schraegstrich zaehlte ``/app/ticketsXY`` als
 * Treffer.
 */
export function activeGroup(pathname: string): NavigationGroup | undefined {
  let best: NavigationEntry | undefined;

  for (const entry of NAVIGATION) {
    const matches = pathname === entry.to || pathname.startsWith(`${entry.to}/`);
    if (matches && (best === undefined || entry.to.length > best.to.length)) {
      best = entry;
    }
  }

  return best?.group;
}
